import { useRef, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { LoadingComponent, components } from '@blaze-cms/react-page-builder';
import { checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import parseHTML from 'html-react-parser';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import Switches from '@blaze-react/switches';
import { useToasts } from '@blaze-react/toaster';
import FormInput from './FormInput';
import { UPDATE_ACADEMIC_DETAILS } from './application/mutations';
import { GET_COUNTRIES } from '../../../../shopware-fe-kp/services/querys';
import { GET_USER } from './application/queries';
import { FORM_SCHEMA } from './constants';
import { normalizeValues } from './helpers/normalize-values';

const UpdateAcademicDetails = ({
  modifier,
  successMessage,
  rejectionMessage,
  informationMessage,
  approvedMessage
}) => {
  const { id: userId } = checkIfLoggedIn();
  const { addToast } = useToasts();
  const formRef = useRef(null);
  const { data = {}, loading: loadingUser } = useQuery(GET_USER, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'network-only'
  });
  const { data: countriesData, countriesLoading } = useQuery(GET_COUNTRIES, {
    variables: { includeOldId: true }
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showTick, setShowTick] = useState(false);
  const [formSchema, setFormSchema] = useState(FORM_SCHEMA);
  const [isDisabled, setIsDisabled] = useState(false);

  const [updateAcademicDetails, { loading }] = useMutation(UPDATE_ACADEMIC_DETAILS, {
    onCompleted: async () => {
      // reset(getValues());
      setShowErrorMessage(false);
      setShowTick(true);
      addToast('Academic details successfully submitted', {
        appearance: 'success',
        autoDismiss: true
      });
      setTimeout(() => setShowTick(false), 2500);
    },
    onError: error => {
      setShowErrorMessage(error.message);
    },
    context: { credentials: 'include' }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset,
    setValue
  } = useForm({
    resolver: ajvResolver(formSchema, {
      strict: false,
      $data: true,
      allErrors: true,
      removeAdditional: true
    }),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    if (!loadingUser && data.user) {
      const { academicProperties = {} } = data.user;
      const allowedKeys = new Set(Object.keys(FORM_SCHEMA.properties));
      const userData = normalizeValues(
        Object.fromEntries(
          Object.entries(academicProperties).filter(([key]) => allowedKeys.has(key))
        )
      );

      if (academicProperties.isRequested) {
        setIsDisabled(true);
      }

      reset(userData, {
        keepDefaultValues: false
      });
    }
  }, [data, loadingUser, reset]);

  useEffect(() => {
    if (!countriesLoading && countriesData && countriesData.countries) {
      const countryOptions = countriesData.countries.map(country => [country.code, country.name]);

      setFormSchema(prevSchema => {
        const updatedSchema = { ...prevSchema };
        updatedSchema.properties.countryId.options = countryOptions;
        return updatedSchema;
      });
    }
  }, [countriesLoading, countriesData]);

  useEffect(() => {
    if (!countriesLoading && !loading && countriesData && data.user) {
      const { countryId } = data.user.academicProperties;
      const { code } =
        countriesData.countries.find(({ oldId }) => oldId && oldId === countryId) || {};
      setValue('countryId', code || '');
    }
  }, [countriesLoading, countriesData, loading, data, setValue]);

  const handleSubmitForm = () => {
    updateAcademicDetails({
      variables: {
        input: getValues()
      }
    });
  };

  if (loadingUser) return <LoadingComponent />;

  const { academicProperties = {} } = data.user || {};
  const parsedModifier = modifier ? ` ${modifier}` : '';

  const formFields = Object.values(formSchema.properties);

  const getButtonContent = () => {
    if (showTick) return 'Saved';
    if (loading) return <LoadingComponent />;
    return 'Save';
  };

  let message = informationMessage;
  if (academicProperties.isApproved) {
    message = approvedMessage;
  } else if (academicProperties.isRejected) {
    message = rejectionMessage;
  } else if (academicProperties.isRequested) {
    message = successMessage;
  }

  const { textblock: TextBlock } = components;
  const showEditSwitch =
    academicProperties.isRequested &&
    !academicProperties.isApproved &&
    !academicProperties.isRejected;
  return (
    <div
      className={`form-container${parsedModifier} academic-details-form ${isDisabled ? 'form-container--disabled' : ''}`}>
      <TextBlock modifier="academic-information-message" html={message} />
      {showEditSwitch && (
        <Switches
          value={!isDisabled}
          label="Edit"
          options={{ label: 'Edit', checked: !!isDisabled }}
          onChange={() => setIsDisabled(!isDisabled)}
        />
      )}
      <form onSubmit={handleSubmit(handleSubmitForm)} className="blaze-user-form" ref={formRef}>
        {formFields.map(field => {
          if (!field.showInForm) return null;
          if (field.text) {
            const infoText = field.text.replace(/\&\{value\}/g, field.value ?? '');
            return (
              <p className="info" key={field.fieldId}>
                <strong>{field.label} </strong> {parseHTML(infoText)}
              </p>
            );
          }
          return (
            <div className="blaze-user-form__input-group" key={field.fieldId}>
              <FormInput
                id={field.fieldId}
                label={field.label}
                isRequired={field.isRequired}
                formFieldType={field.formFieldType}
                register={register}
                placeholder={field.placeholder}
                error={errors[field.fieldId]}
                options={field.options}
                isDisabled={isDisabled}
              />
            </div>
          );
        })}

        {!isDisabled && (
          <button
            className={`button blaze-user-form__submit-button ${
              !isDirty ? 'blaze-user-form__submit-button--condensed' : ''
            }`}
            type="submit"
            disabled={showTick || isDisabled}>
            {getButtonContent()}
          </button>
        )}

        {showErrorMessage && (
          <div className="form error_message_wrapper">
            <div className="error_message_icon" />
            <div className="error_message">{showErrorMessage}</div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UpdateAcademicDetails;
